// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useRef, useState } from 'react';
import type { IntelligenceUseCaseCode } from '../../../api/intelligenceApi';
import { intelligenceApi, sendMessageAwaitingReply } from '../../../api/intelligenceApi';
import { proposalApi } from '../../../api/proposalApi';
import type { ProposalSection } from '../../../types/proposal';
import { parseAiJsonObject } from './aiJson';

/** Inline error shown when the intelligence service can't be reached. */
export const AI_UNAVAILABLE_MESSAGE = 'AI drafting unavailable — edit manually.';
/** Inline error shown when the assistant's reply isn't the expected JSON. */
export const AI_PARSE_ERROR_MESSAGE =
  'The AI returned an unexpected response — try again or edit manually.';

export type UseAiBlockDraftOptions = Readonly<{
  proposalId: string;
  /** Anchors the drafting session to the relationship, when known. */
  profileId?: string;
  sectionKey: string;
  /** Block name within the section (backend apply-ai contract). */
  block: string;
  /**
   * Intelligence use case the session is created under. Defaults to
   * `SOS_BUILDER` (the pension proposal blocks); the Breakthrough Report's
   * authored sections use `REPORT_BUILDER` instead (BR-P6).
   */
  useCase?: IntelligenceUseCaseCode;
  /** Builds the chat message; receives the trimmed steering instruction. */
  buildMessage: (instruction: string | undefined) => string;
  /**
   * Validates + narrows the parsed JSON object into the apply-ai payload.
   * Throw to reject an unexpected shape (surfaces as a parse error).
   */
  buildPayload: (parsed: Record<string, unknown>) => Record<string, unknown>;
  /** Receives the section DTO the apply-ai endpoint returns. */
  onApplied: (updated: ProposalSection) => void;
}>;

/**
 * Shared "Generate with AI" behaviour for the builder sections (P3, extended
 * to the Breakthrough Report's REPORT_BUILDER use case in BR-P6): mirrors
 * wizard v1's WizardStepSosBuilder pattern — one intelligence session per
 * hook instance (created lazily under `useCase`, reused across generates),
 * an optional steering instruction, `sendMessageAwaitingReply` for the
 * reply, then `proposalApi.applyAiBlock` to persist. AI assistance is an
 * accelerator, never a dependency — every failure resolves to an inline
 * message and manual editing stays available.
 */
export function useAiBlockDraft({
  proposalId,
  profileId,
  sectionKey,
  block,
  useCase = 'SOS_BUILDER',
  buildMessage,
  buildPayload,
  onApplied,
}: UseAiBlockDraftOptions) {
  const [instruction, setInstruction] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const generate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      if (!sessionIdRef.current) {
        const session = await intelligenceApi.createSession(useCase, profileId);
        sessionIdRef.current = session.sessionId;
      }
      const sessionId = sessionIdRef.current;
      const trimmed = instruction.trim();
      const reply = await sendMessageAwaitingReply(
        sessionId,
        buildMessage(trimmed.length > 0 ? trimmed : undefined),
      );

      let payload: Record<string, unknown>;
      try {
        payload = buildPayload(parseAiJsonObject(reply));
      } catch {
        setError(AI_PARSE_ERROR_MESSAGE);
        return;
      }

      const updated = await proposalApi.applyAiBlock(
        proposalId,
        sectionKey,
        block,
        payload,
        sessionId,
      );
      onApplied(updated);
    } catch {
      setError(AI_UNAVAILABLE_MESSAGE);
    } finally {
      setGenerating(false);
    }
  }, [
    generating,
    instruction,
    profileId,
    useCase,
    buildMessage,
    buildPayload,
    proposalId,
    sectionKey,
    block,
    onApplied,
  ]);

  return { instruction, setInstruction, generating, error, generate };
}

export default useAiBlockDraft;
